import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  CheckCircle, XCircle, Clock, Users, UserCheck, UserX, 
  Eye, Loader2, RefreshCw, Phone, MapPin, Briefcase
} from "lucide-react";

const AdminPage = () => {
  useAuth(); // Ensure user is authenticated
  const _navigate = useNavigate();
  
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        api.get("/admin/pending-users"),
        api.get("/admin/all-users")
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
    } catch (_error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleVerify = async (userId, status, note = "") => {
    setActionLoading(true);
    try {
      await api.post("/admin/verify-user", {
        user_id: userId,
        status,
        note
      });
      toast.success(`User ${status === "verified" ? "verified" : "rejected"} successfully`);
      setShowUserDialog(false);
      setSelectedUser(null);
      setRejectionNote("");
      fetchData();
    } catch (_error) {
      toast.error("Failed to update user status");
    } finally {
      setActionLoading(false);
    }
  };

  const getInitials = (u) => {
    if (u.first_name) {
      return `${u.first_name[0]}${(u.last_name || "L")[0]}`.toUpperCase();
    }
    return u.phone?.slice(-2) || "??";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "verified":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" /> Verified</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
    }
  };

  const stats = {
    total: allUsers.length,
    pending: allUsers.filter(u => u.verification_status === "pending" && u.is_profile_complete).length,
    verified: allUsers.filter(u => u.verification_status === "verified").length,
    rejected: allUsers.filter(u => u.verification_status === "rejected").length
  };

  const UserCard = ({ u, showActions = false }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Avatar className="w-12 h-12">
            <AvatarImage src={u.avatar} />
            <AvatarFallback className="bg-primary/10 text-primary font-semibold">
              {getInitials(u)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold truncate">
                {u.first_name} {u.last_name || "LUNAWAT"}
              </h3>
              {getStatusBadge(u.verification_status)}
            </div>
            
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" /> {u.phone}
              </span>
              {u.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {u.city}, {u.state}
                </span>
              )}
              {u.occupation && (
                <span className="flex items-center gap-1 capitalize">
                  <Briefcase className="w-3 h-3" /> {u.occupation}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedUser(u);
                setShowUserDialog(true);
              }}
              data-testid={`view-user-${u.id}`}
            >
              <Eye className="w-4 h-4 mr-1" /> View
            </Button>
            
            {showActions && u.verification_status === "pending" && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleVerify(u.id, "verified")}
                  disabled={actionLoading}
                  data-testid={`verify-user-${u.id}`}
                >
                  <CheckCircle className="w-4 h-4" />
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setSelectedUser(u);
                    setShowUserDialog(true);
                  }}
                  disabled={actionLoading}
                  data-testid={`reject-user-${u.id}`}
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-6 px-4" data-testid="admin-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.verified}</p>
                <p className="text-sm text-muted-foreground">Verified</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.rejected}</p>
                <p className="text-sm text-muted-foreground">Rejected</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Lists */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({stats.pending})
            </TabsTrigger>
            <TabsTrigger value="verified" data-testid="tab-verified">
              Verified ({stats.verified})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">
              All Users ({stats.total})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : pendingUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No pending verifications</p>
                </CardContent>
              </Card>
            ) : (
              pendingUsers.map(u => (
                <UserCard key={u.id} u={u} showActions={true} />
              ))
            )}
          </TabsContent>

          <TabsContent value="verified" className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : allUsers.filter(u => u.verification_status === "verified").length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No verified users yet</p>
                </CardContent>
              </Card>
            ) : (
              allUsers.filter(u => u.verification_status === "verified").map(u => (
                <UserCard key={u.id} u={u} />
              ))
            )}
          </TabsContent>

          <TabsContent value="all" className="space-y-3">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : allUsers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No users registered yet</p>
                </CardContent>
              </Card>
            ) : (
              allUsers.map(u => (
                <UserCard key={u.id} u={u} />
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* User Detail Dialog */}
        <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedUser && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={selectedUser.avatar} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {getInitials(selectedUser)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span>{selectedUser.first_name} {selectedUser.last_name || "LUNAWAT"}</span>
                      <div className="mt-1">{getStatusBadge(selectedUser.verification_status)}</div>
                    </div>
                  </DialogTitle>
                  <DialogDescription>
                    Registered on {formatDate(selectedUser.created_at)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* Personal Info */}
                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Personal Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{selectedUser.phone}</span></div>
                      <div><span className="text-muted-foreground">Gender:</span> <span className="font-medium capitalize">{selectedUser.gender || "N/A"}</span></div>
                      <div><span className="text-muted-foreground">DOB:</span> <span className="font-medium">{formatDate(selectedUser.date_of_birth)}</span></div>
                      <div><span className="text-muted-foreground">Username:</span> <span className="font-medium">@{selectedUser.username || "N/A"}</span></div>
                    </div>
                  </div>

                  {/* Family Info */}
                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Family Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Marital Status:</span> <span className="font-medium capitalize">{selectedUser.marital_status || "N/A"}</span></div>
                      {selectedUser.spouse_name && <div><span className="text-muted-foreground">Spouse:</span> <span className="font-medium">{selectedUser.spouse_name}</span></div>}
                      <div><span className="text-muted-foreground">Children:</span> <span className="font-medium">{selectedUser.number_of_children || 0}</span></div>
                      {selectedUser.father_name && <div><span className="text-muted-foreground">Father:</span> <span className="font-medium">{selectedUser.father_name}</span></div>}
                      {selectedUser.mother_name && <div><span className="text-muted-foreground">Mother:</span> <span className="font-medium">{selectedUser.mother_name}</span></div>}
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Address</h4>
                    <p className="text-sm">
                      {selectedUser.address_line1 && (
                        <>
                          {selectedUser.address_line1}
                          {selectedUser.address_line2 && <>, {selectedUser.address_line2}</>}
                          <br />
                          {selectedUser.city}, {selectedUser.state} - {selectedUser.pincode}
                          <br />
                          {selectedUser.country}
                        </>
                      ) || "Not provided"}
                    </p>
                  </div>

                  {/* Business Info */}
                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Business Information</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Occupation:</span> <span className="font-medium capitalize">{selectedUser.occupation || "N/A"}</span></div>
                      {selectedUser.company_name && <div><span className="text-muted-foreground">Company:</span> <span className="font-medium">{selectedUser.company_name}</span></div>}
                      {selectedUser.designation && <div><span className="text-muted-foreground">Designation:</span> <span className="font-medium">{selectedUser.designation}</span></div>}
                    </div>
                    {selectedUser.work_address && (
                      <p className="text-sm mt-2"><span className="text-muted-foreground">Work Address:</span> {selectedUser.work_address}</p>
                    )}
                  </div>

                  {/* Rejection Note Input */}
                  {selectedUser.verification_status === "pending" && (
                    <div>
                      <h4 className="font-semibold mb-2 text-sm text-muted-foreground uppercase">Rejection Note (if rejecting)</h4>
                      <Textarea
                        placeholder="Enter reason for rejection (optional)"
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        rows={2}
                        data-testid="rejection-note"
                      />
                    </div>
                  )}
                </div>

                <DialogFooter>
                  {selectedUser.verification_status === "pending" && (
                    <>
                      <Button
                        variant="destructive"
                        onClick={() => handleVerify(selectedUser.id, "rejected", rejectionNote)}
                        disabled={actionLoading}
                        data-testid="confirm-reject-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                        Reject
                      </Button>
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleVerify(selectedUser.id, "verified")}
                        disabled={actionLoading}
                        data-testid="confirm-verify-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                        Verify User
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={() => setShowUserDialog(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminPage;
